import { ProgramNames } from '../../../models/programs/Programs'
import { PROVIDER_TYPES } from '../../../models/providers/Providers'
import { PROGRAM_TAXONOMY, ProgramCustomerPropertyId } from '../../common/constants/customerProgram'
import { TREATMENT_OPTIONS } from '../../common/constants/treatmentOptions'
import { LTSessionTypes } from '../../sessions/constants'
import { ConsentText, ProgramDisplayLabels } from '../messages'
import { ProgramConfig } from '../types'

const DIRECT_ACCESS_PROGRAM_DEFAULTS = {
  customerPropertyId: ProgramCustomerPropertyId.directAccessTherapy,
  associatedProviderTypes: [PROVIDER_TYPES.THERAPIST.lyraType],
  selfPayEligible: false,
  isDAEngageProgram: true,
  consents: [
    {
      consentText: ConsentText.therapyConsent,
      consentUrl: 'https://www.lyrahealth.com/lt-agreement',
      showConsentIf: () => true,
    },
  ],
  noteContentMetadataName: {
    INITIAL_NOTE: 'daInitialNote',
    SESSION_NOTE: 'daSessionNote',
    GENERAL_NOTE: 'daGeneralNote',
    REFERRAL_NOTE: 'baseReferralNote',
  },
} satisfies Partial<ProgramConfig>

export const DIRECT_ACCESS_COUPLES_THERAPY_PROGRAM = {
  ...DIRECT_ACCESS_PROGRAM_DEFAULTS,
  programName: ProgramNames.DirectAccessCouplesTherapy,
  blendedCareProgramId: '690f8981-70c3-4714-b667-464da34cd1c2',

  displayLabels: {
    default: ProgramDisplayLabels.couplesTherapy,
    short: 'Couples',
    daClientList: 'Couples Therapy',
    sessionSummary: 'couples therapy',
  },

  associatedProviderTypes: [PROVIDER_TYPES.THERAPIST.lyraType],
  associatedProviderModalities: ['couples'],

  directBookingParams: {
    clientele: PROGRAM_TAXONOMY.clientele.couples,
    treatment: PROGRAM_TAXONOMY.treatment.therapy,
    partner: PROGRAM_TAXONOMY.partner.directAccess,
    offering: PROGRAM_TAXONOMY.offering.default,
  },

  treatmentOptionProperties: {
    associatedTriageTreatmentOptions: [TREATMENT_OPTIONS.COUPLES_THERAPY],
    isFullTreatmentOption: false,
  },

  sessionTypeConfig: {
    sessionType: LTSessionTypes.INITIAL_DA_COUPLES_SESSION,
    followupSessionType: LTSessionTypes.FOLLOWUP_DA_COUPLES_SESSION,
    rateStrategy: 'differentialPricingCalculation',
  },

  canRescheduleAppointment: true,
} satisfies ProgramConfig

export const DIRECT_ACCESS_INDIVIDUAL_THERAPY_PROGRAM = {
  ...DIRECT_ACCESS_PROGRAM_DEFAULTS,
  programName: ProgramNames.DirectAccessIndividualTherapy,
  blendedCareProgramId: 'affa3537-78a4-4abf-b330-b7abc1a5740b',

  displayLabels: {
    default: ProgramDisplayLabels.therapy,
    short: 'Individual',
    daClientList: 'Adult Therapy',
    sessionSummary: 'therapy',
  },

  associatedProviderTypes: [PROVIDER_TYPES.THERAPIST.lyraType],
  associatedProviderModalities: ['individual'],

  treatmentOptionProperties: {
    associatedTriageTreatmentOptions: [TREATMENT_OPTIONS.INDIVIDUAL_THERAPY],
    isFullTreatmentOption: false,
  },

  sessionTypeConfig: {
    sessionType: LTSessionTypes.INITIAL_DA_SESSION,
    followupSessionType: LTSessionTypes.FOLLOWUP_DA_SESSION,
    minorSessionType: LTSessionTypes.INITIAL_DA_CHILD_SESSION,
    minorFollowupSessionType: LTSessionTypes.FOLLOWUP_DA_CHILD_SESSION,
    rateStrategy: 'differentialPricingCalculation',
  },

  canRescheduleAppointment: true,
} satisfies ProgramConfig

export const DIRECT_ACCESS_FAMILY_THERAPY_PROGRAM = {
  ...DIRECT_ACCESS_PROGRAM_DEFAULTS,
  programName: ProgramNames.DirectAccessFamilyTherapy,
  blendedCareProgramId: '9cc004d9-614c-4a95-8176-d1ac89e5628a',

  displayLabels: {
    default: ProgramDisplayLabels.familyTherapy,
    short: 'Family',
    daClientList: 'Family Therapy',
    sessionSummary: 'family therapy',
  },

  associatedProviderTypes: [PROVIDER_TYPES.THERAPIST.lyraType],
  associatedProviderModalities: ['family'],

  directBookingParams: {
    clientele: PROGRAM_TAXONOMY.clientele.family,
    treatment: PROGRAM_TAXONOMY.treatment.therapy,
    partner: PROGRAM_TAXONOMY.partner.directAccess,
    offering: PROGRAM_TAXONOMY.offering.default,
  },

  treatmentOptionProperties: {
    associatedTriageTreatmentOptions: [TREATMENT_OPTIONS.FAMILY_THERAPY],
    isFullTreatmentOption: false,
  },

  sessionTypeConfig: {
    sessionType: LTSessionTypes.INITIAL_DA_FAMILY_SESSION,
    followupSessionType: LTSessionTypes.FOLLOWUP_DA_FAMILY_SESSION,
    rateStrategy: 'differentialPricingCalculation',
  },

  hidesEligibleServicesForMinors: true,
  canRescheduleAppointment: true,
} satisfies ProgramConfig

export const DIRECT_ACCESS_INDIVIDUAL_ADOLESCENT_THERAPY_PROGRAM = {
  ...DIRECT_ACCESS_PROGRAM_DEFAULTS,
  programName: ProgramNames.DirectAccessIndividualAdolescentTherapy,
  blendedCareProgramId: 'c7034a1f-f506-4349-afab-69e173052238',
  isTeenProgram: true,

  displayLabels: {
    default: ProgramDisplayLabels.teensTherapy,
    careType: ProgramDisplayLabels.therapy,
    short: 'Individual',
    clientProfile: 'Teen Therapy',
    daClientList: 'Teen Therapy',
    sessionSummary: 'adolescent therapy',
  },

  associatedProviderTypes: [PROVIDER_TYPES.THERAPIST.lyraType],
  associatedProviderModalities: ['individual'],

  treatmentOptionProperties: {
    associatedTriageTreatmentOptions: [TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_CHILD],
    isFullTreatmentOption: false,
  },

  sessionTypeConfig: {
    sessionType: LTSessionTypes.INITIAL_DA_CHILD_SESSION,
    followupSessionType: LTSessionTypes.FOLLOWUP_DA_CHILD_SESSION,
    rateStrategy: 'differentialPricingCalculation',
  },

  canRescheduleAppointment: true,
} satisfies ProgramConfig

export const DIRECT_ACCESS_INDIVIDUAL_CHILD_THERAPY_PROGRAM = {
  ...DIRECT_ACCESS_PROGRAM_DEFAULTS,
  programName: ProgramNames.DirectAccessIndividualChildTherapy,
  blendedCareProgramId: 'd125cead-10a1-4d90-9f9f-457ca3490e91',
  isChildProgram: true,

  displayLabels: {
    default: ProgramDisplayLabels.childTherapy,
    careType: ProgramDisplayLabels.therapy,
    short: 'Individual',
    daClientList: 'Child Therapy',
    sessionSummary: 'child therapy',
  },

  associatedProviderTypes: [PROVIDER_TYPES.THERAPIST.lyraType],
  associatedProviderModalities: ['individual'],

  treatmentOptionProperties: {
    associatedTriageTreatmentOptions: [TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_CHILD],
    isFullTreatmentOption: false,
  },

  sessionTypeConfig: {
    sessionType: LTSessionTypes.INITIAL_DA_CHILD_SESSION,
    followupSessionType: LTSessionTypes.FOLLOWUP_DA_CHILD_SESSION,
    rateStrategy: 'differentialPricingCalculation',
  },

  canRescheduleAppointment: true,
} satisfies ProgramConfig
